/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable no-restricted-globals */
import { FunctionComponent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  Checkbox,
  Layout,
  RadioButton,
  Select as SelectBR,
} from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { t, TFunction } from 'i18next';
import { FormField } from '../../../components/FormField/FormField';
import { NumericFormField } from '../../../components/FormFields/NumericFormField';
import { SelectFormField } from '../../../components/FormFields/SelectFormField';
import { StaticTextFormField } from '../../../components/FormFields/StaticTextFormField';
import {
  damagesWaiver,
  milesPackageListOptions,
  tireCountOptionsList,
  warrantyExtensionNewCarDurationOptons,
  warrantyExtensionOldCarDurationOptons,
  WarrantyExtensionTypeEnum,
  warrantyExtensionTypeOptions,
  WarrantyExtensionTypeType,
} from '../../../resources/base/models';
import {
  distributionModelsOptionsBusiness,
  distributionModelsOptionsPrivate,
} from '../../../resources/base/models/distributionModel';
import { CustomerType, InfoToRenderType } from '../../../resources/base/models/enums';
import { isProductForNewCar } from '../../../resources/base/models/financialProduct';
import { Analytics } from '../../../services/analytics';
import { getPrintAndSendEmailButtonsEvent } from '../../../services/analytics/analyticsEvents';
import { editQuote, validateQuote } from '../../../services/api/miles/miles-services';
import {
  assertDefined,
  assertDefinedVoid,
  getEnumValues,
  getLabelFromOptionsLit,
} from '../../../services/common/miscUtils';
import { SetState } from '../../../services/common/miscUtils.types';
import { staticSelector } from '../../../services/redux';
import {
  isQuoteUnEditable,
  quoteSelectors,
  updateQuoteIdAndStatus,
} from '../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { QuotationFormData } from '../../../types';
import { preventCopyQuoteStatuses } from '../../utils';
import { calculateContractMileageAfterDurationChange } from './calculations';
import { FuelService, isFuelTypeProduct, isNonTollBadgeFuelProduct } from './FuelService';
import {
  adjustMileageToAllowedRange,
  getAdditionalProductLabel,
  InsuranceCustomerQualifierId,
  InsuranceServiceProductId,
  isChoiceAnExclusionOfService,
  isChoiceAnInclusionOfService,
  isWarrantyExtensionAdditionalProduct,
  TireServiceName,
} from './utils';

interface Props {
  t: TFunction;
  values?: any;
  formData?: any;
  setFieldValue?: any;
  openModal?: any;
  setOpenModal?: any;
  infoToRender?: any;
  setInfoToRender?: any;
  docPrintingList?: any;
  onChange?: any;
  onBlur?: any;
  readOnly?: any;
  value?: any;
  customerType?: any;
  name?: any;
  modalInfo?: any;
  disabled?: any;
  onClick?: any;
  optionList?: any;
  label?: any;
  durationOptions?: number[];
  isDisabled?: boolean;
  mileageAllowedValues?: number[];
}

export const buildModalTitle = (t: any, infoToRender?: InfoToRenderType) => {
  switch (infoToRender) {
    case InfoToRenderType.SUMMARY_OF_VEHICLE:
      return t('step3:vehicleDetails:label');
    case InfoToRenderType.LCD_WARNING:
      return t('step3:lcdProductWarning:title');
    case InfoToRenderType.PUSH_VO_WARNING:
      return t('step3:pushVoWarning:title');
    default:
      return '';
  }
};

export const buildModalConfirmText = (t: any, infoToRender?: InfoToRenderType) => {
  switch (infoToRender) {
    case InfoToRenderType.SUMMARY_OF_VEHICLE:
      return t('step3:vehicleDetails:confirm');
    case InfoToRenderType.MILEAGE_PRICE_AMOUNT:
      return t('step3:excessMileagePriceAmount:confirm');
    case InfoToRenderType.BONUS_DEALER_PART:
      return t('step3:dealerParticipationConfirm:label');
    case InfoToRenderType.DOCUMENTS_PRINTING:
      return t('step3:printConfirm:label');
    case InfoToRenderType.LCD_WARNING:
      return t('step3:lcdProductWarning:confirm');
    case InfoToRenderType.PUSH_VO_WARNING:
      return t('step3:pushVoWarning:confirm');
    default:
      return t('step3:services:apply');
  }
};

export const getModalCenterContent = (infoToRender?: InfoToRenderType) =>
  infoToRender === InfoToRenderType.LCD_WARNING || infoToRender === InfoToRenderType.PUSH_VO_WARNING;

export const ModalOpenButton: FunctionComponent<Props & { icon?: string; link?: boolean; text?: string }> = ({
  disabled,
  setOpenModal,
  setInfoToRender,
  name,
  modalInfo,
  icon,
  link,
  text,
}) => {
  return (
    <Button
      ariaLabel={`modal-${name ?? modalInfo}-button`}
      disabled={disabled}
      icon={icon ?? 'semantic-plus'}
      secondary
      round
      className="u-mt-xsmall"
      small
      link={link}
      // eslint-disable-next-line react/no-children-prop
      children={text}
      onClick={async () => {
        setOpenModal(true);
        setInfoToRender(modalInfo);
      }}
      type="button"
    />
  );
};

export const DistributionModel: FunctionComponent<Props> = ({ t, customerType, disabled, setFieldValue }) => {
  const distributionModel =
    customerType === CustomerType.INDIVIDUAL ? distributionModelsOptionsPrivate : distributionModelsOptionsBusiness;

  return (
    <FormField
      required
      type="select"
      name="distributionModel"
      labelText={t('step3:distributionModel:label')}
      render={(fieldProps) => (
        <SelectBR
          {...fieldProps}
          disabled={disabled}
          onChange={({ target }) => setFieldValue('distributionModel', target.value)}
        >
          {distributionModel?.map(({ label, value }) => (
            <SelectBR.Item key={`${label}`} value={`${value}`}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

export const Duration: FunctionComponent<Props> = ({ disabled, durationOptions, mileageAllowedValues }) => {
  const { values, setFieldValue } = useFormikContext<QuotationFormData>();
  const _durationOptions =
    durationOptions?.map((value) => ({
      value: value.toString(),
      label: value.toString(),
    })) || [];

  return (
    <SelectFormField
      fieldName="duration"
      labelTranslationKey="step3:duration:label"
      dropDownOptionsList={_durationOptions}
      isDisabled={disabled}
      onChange={(selectedValue) => {
        const newDuration = Number(selectedValue.target.value);
        const newContractMileage = calculateContractMileageAfterDurationChange(
          values.contractMileage,
          newDuration,
          values.duration
        );
        setFieldValue('contractMileage', adjustMileageToAllowedRange(newContractMileage, mileageAllowedValues));
      }}
    />
  );
};

export const DamagesWaiver: FunctionComponent<{ disabled: boolean }> = ({ disabled }) => {
  return (
    <SelectFormField
      fieldName="damagesWaiver"
      isDisabled={disabled}
      labelTranslationKey="step3:excessMileagePriceAmount:damagesWaiver"
      dropDownOptionsList={damagesWaiver}
      optional
    />
  );
};

export const TireCountSummer: FunctionComponent<{ disabled: boolean }> = ({ disabled }) => {
  return (
    <SelectFormField
      fieldName="tireCountSummer"
      labelTranslationKey="step3:tireServiceModal:tireCountSummer"
      dropDownOptionsList={tireCountOptionsList}
      isDisabled={disabled}
    />
  );
};

export const TireCountWinter: FunctionComponent<{ disabled: boolean }> = ({ disabled }) => {
  return (
    <SelectFormField
      fieldName="tireCountWinter"
      labelTranslationKey="step3:tireServiceModal:tireCountWinter"
      dropDownOptionsList={tireCountOptionsList}
      isDisabled={disabled}
    />
  );
};

export const TireCountAllSeason: FunctionComponent<{ disabled: boolean }> = ({ disabled }) => {
  return (
    <SelectFormField
      fieldName="tireCountWinter"
      labelTranslationKey="step3:tireServiceModal:tireCountAllSeasons"
      dropDownOptionsList={tireCountOptionsList}
      isDisabled={disabled}
    />
  );
};

type MilesPackageProps = {
  disabled: boolean;
  calculate: (newMilesPackageValue: QuoteSchema.Schemas.MilesPackageEnumType) => void;
};

export const MilesPackage: FunctionComponent<MilesPackageProps> = ({ disabled, calculate }) => {
  return (
    <SelectFormField
      fieldName="milesPackage"
      labelTranslationKey="step3:milesPackage:label"
      isDisabled={disabled}
      dropDownOptionsList={milesPackageListOptions}
      optional
      onChange={(e) => {
        calculate(e.target.value as QuoteSchema.Schemas.MilesPackageEnumType);
      }}
    />
  );
};

export const TotalAmountInsAndServ: FunctionComponent<Props> = () => (
  <NumericFormField
    fieldName="totalAmountInsAndServ"
    labelTranslationKey="step3:totalAmountInsAndServ:label"
    type="money"
    maximumFractionDigits={2}
    isDisabled
    optional
  />
);

const TireCount: FunctionComponent<{ name: string | undefined; isChecked: boolean; disabled: boolean }> = ({
  name,
  isChecked,
  disabled,
}) => {
  if (!name || !isChecked) return null;

  return (
    <Layout.Item default="1/4" s="1/2" className="u-mt-small u-ml-large u-pl-small">
      {name === TireServiceName.SUMMER_TIRES && <TireCountSummer disabled={disabled} />}
      {name === TireServiceName.WINTER_TIRES && <TireCountWinter disabled={disabled} />}
      {name === TireServiceName.ALL_SEASON_TIRES && <TireCountAllSeason disabled={disabled} />}
    </Layout.Item>
  );
};

const WarrantyExtensionAdditionalProductDetails: FunctionComponent<{
  disabled: boolean;
  isChecked: boolean;
}> = ({ disabled, isChecked }) => {
  const { values, setFieldValue } = useFormikContext<QuotationFormData>();

  const productName = useSelector(quoteSelectors.selectProductName);

  const durationOptions = isProductForNewCar(productName!)
    ? warrantyExtensionNewCarDurationOptons
    : warrantyExtensionOldCarDurationOptons;

  const milesWarrantyType = useSelector(quoteSelectors.selectMilesWarrantyType) as WarrantyExtensionTypeType;
  // Value to use if prefill based on API data dit not
  const milesWarrantyTypeFallback = isProductForNewCar(productName!)
    ? WarrantyExtensionTypeEnum[700011]
    : WarrantyExtensionTypeEnum[700012];

  useEffect(() => {
    if (isChecked && !values.milesWarrantyTypeDuration) {
      setFieldValue('milesWarrantyTypeDuration', durationOptions[0].value);
    } else if (!isChecked) {
      setFieldValue('milesWarrantyTypeDuration', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationOptions, values.milesWarrantyTypeDuration, isChecked]);

  if (!isChecked) return null;

  return (
    <Layout className="u-ml">
      <Layout.Item default="1/4" s="1/2" className="u-mt-small u-ml u-pl-small">
        <StaticTextFormField
          fieldName="_milesWarrantyType"
          isReadonly
          isDisabled={disabled}
          labelTranslationKey="step3:milesWarrantyType:label"
          displayValue={getLabelFromOptionsLit(
            warrantyExtensionTypeOptions,
            milesWarrantyType ?? milesWarrantyTypeFallback
          )}
        />
      </Layout.Item>
      <Layout.Item default="1/4" s="1/2" className="u-mt-small u-ml u-pl-small">
        <SelectFormField
          fieldName="milesWarrantyTypeDuration"
          labelTranslationKey="step3:milesWarrantyTypeDuration:label"
          dropDownOptionsList={durationOptions}
          isDisabled={disabled}
        />
      </Layout.Item>
    </Layout>
  );
};

interface ServicesProps {
  disabled: boolean;
  selectedAdditionalProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[];
  setSelectedAllAdditionalProducts: SetState<QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[]>;
  setSelectedInsuranceApplicantOption: SetState<InsuranceCustomerQualifierId>;
  selectedInsuranceApplicantOption: InsuranceCustomerQualifierId;
  customerType: CustomerType;
  fuelDepositAmount: number;
  managementFees: number;
  resetFuelPrices: () => void;
  hasCoCustomer: boolean;
}

export const Services: FunctionComponent<ServicesProps> = ({
  disabled,
  selectedAdditionalProducts,
  setSelectedAllAdditionalProducts,
  customerType,
  managementFees,
  fuelDepositAmount,
  resetFuelPrices,
  setSelectedInsuranceApplicantOption,
  selectedInsuranceApplicantOption,
  hasCoCustomer,
}) => {
  const { setFieldValue } = useFormikContext<QuotationFormData>();

  const isChoiceSelected = (
    serviceName: string | undefined,
    choiceId: string | undefined,
    list: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[]
  ) => {
    return list.some(
      ({ choices, name }) =>
        name === serviceName && choices?.some((choice) => choice.isSelected && choice.id === choiceId)
    );
  };

  const selectAdditionalProduct = (serviceName: string | undefined, choiceId: string | undefined) => {
    const nextState = structuredClone(
      selectedAdditionalProducts
    ) as QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[];

    const selectedProduct = nextState.find(({ name, choices }) => {
      return serviceName === name && choices?.some((choice) => choice.id === choiceId);
    });

    selectedProduct?.choices?.forEach((choice) => {
      choice.isSelected = choice.id === choiceId;
    });

    setSelectedAllAdditionalProducts(nextState);
  };

  const tireServiceNames: TireServiceName[] = getEnumValues(TireServiceName);

  const tireProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[] = selectedAdditionalProducts.filter(
    ({ name }) => tireServiceNames.includes(name as TireServiceName)
  );

  const nonTireAndNonFuelProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[] =
    selectedAdditionalProducts.filter(
      ({ name }) => !tireServiceNames.includes(name as TireServiceName) && !isFuelTypeProduct(name)
    );

  const shouldFuelServiceBeVisible =
    customerType === CustomerType.LEGAL_ENTITY &&
    selectedAdditionalProducts.some(({ name }) => isNonTollBadgeFuelProduct(name));

  const updateTireCountField = (
    name: string | undefined,
    setFieldValue: (field: string, value: any) => void,
    value: string
  ) => {
    const isTireService = tireServiceNames.includes(name as TireServiceName);
    if (!isTireService) return;

    switch (name) {
      case TireServiceName.SUMMER_TIRES:
        setFieldValue('tireCountSummer', value);
        break;
      case TireServiceName.WINTER_TIRES:
        setFieldValue('tireCountWinter', value);
        break;
      case TireServiceName.ALL_SEASON_TIRES:
        setFieldValue('tireCountAllSeasons', value);
        break;
      default:
        break;
    }
  };

  const handleCheckboxToggle = (name: string | undefined, choices?: QuoteSchema.Schemas.ChoicesGetQuoteItem[]) => {
    const relevantChoices = choices?.filter(isChoiceAnInclusionOfService);
    const notIncludedChoice = choices?.filter(isChoiceAnExclusionOfService);
    const isChecked = relevantChoices?.some((choice) => choice.isSelected);

    if (isChecked) {
      // Select "Not included" option
      notIncludedChoice?.forEach((choice) => {
        selectAdditionalProduct(name, choice.id);
      });
      updateTireCountField(name, setFieldValue, '');
    } else {
      // Select first choice
      const firstChoice = relevantChoices?.[0];
      if (firstChoice?.id) {
        selectAdditionalProduct(name, firstChoice.id);
      }
      // Set default value after checkbox is checked
      updateTireCountField(name, setFieldValue, '2');
    }
  };

  const renderPersonalCreditInsuranceNumberOfPeopleButtons = (
    choice: QuoteSchema.Schemas.ChoicesGetQuoteItem,
    _isChoiceSelected: boolean
  ) => {
    const isPersonalCreditInsuranceChoice = choice.id === InsuranceServiceProductId.PERSONAL_CREDIT_INSURANCE_PRIVATE;
    const showPersonalCreditInsuranceNumberOfPeopleButtons =
      isPersonalCreditInsuranceChoice && _isChoiceSelected && hasCoCustomer;

    if (!showPersonalCreditInsuranceNumberOfPeopleButtons) return null;

    return (
      <Layout.Item className="u-mt-small">
        <Layout>
          <Layout.Item default="1/1">
            <RadioButton
              disabled={disabled}
              className="u-mt-small"
              id={`${choice.id}-option1`}
              name={`${choice.id}-options`}
              defaultChecked={selectedInsuranceApplicantOption === InsuranceCustomerQualifierId.APPLICANT}
              onChange={() => {
                setSelectedInsuranceApplicantOption(InsuranceCustomerQualifierId.APPLICANT);
              }}
            >
              {t('step3:insuranceCustomerQualifier:applicant')}
            </RadioButton>
          </Layout.Item>
          <Layout.Item default="1/1">
            <RadioButton
              disabled={disabled}
              className="u-mt-small"
              id={`${choice.id}-option2`}
              name={`${choice.id}-options`}
              defaultChecked={selectedInsuranceApplicantOption === InsuranceCustomerQualifierId.CO_APPLICANT}
              onChange={() => setSelectedInsuranceApplicantOption(InsuranceCustomerQualifierId.CO_APPLICANT)}
            >
              {t('step3:insuranceCustomerQualifier:coApplicant')}
            </RadioButton>
          </Layout.Item>
        </Layout>
      </Layout.Item>
    );
  };

  const renderChoice = (
    choice: QuoteSchema.Schemas.ChoicesGetQuoteItem,
    productName: string | undefined,
    layoutFraction: '1/5' | '1/4' | '1/3'
  ) => {
    const _isChoiceSelected = isChoiceSelected(productName, choice.id, selectedAdditionalProducts);

    return (
      <Layout.Item key={`${productName} ${choice.id}`} default={layoutFraction} s="1/1" className="u-ml-small">
        <RadioButton
          className="u-ml-small u-mt-small"
          disabled={disabled}
          id={`${productName} ${choice.id}`}
          onChange={() => {
            selectAdditionalProduct(productName, choice.id);
          }}
          defaultChecked={_isChoiceSelected}
          name={productName}
        >
          {choice.name}
          {renderPersonalCreditInsuranceNumberOfPeopleButtons(choice, _isChoiceSelected)}
        </RadioButton>
      </Layout.Item>
    );
  };

  const renderAdditionalProduct = (additionalProduct: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem) => {
    const { name, choices } = additionalProduct;

    assertDefinedVoid(name, 'Additional product name was undefined.');

    const relevantChoices = choices?.filter(isChoiceAnInclusionOfService) ?? [];
    const isChecked = relevantChoices.some((choice) => choice.isSelected);
    const isTireProduct = tireServiceNames.includes(name as TireServiceName);
    const choicesToRender = !isChecked || isTireProduct ? null : relevantChoices;
    const layoutFraction = (choicesToRender?.length ?? 0 > 3) ? '1/5' : '1/4';
    const hasMultipleRelevantChoices = (relevantChoices?.length ?? 0) > 1;
    const shouldUseGeneralLabel = isTireProduct || hasMultipleRelevantChoices;

    return (
      <Layout.Item key={name} className="u-pl-none" default="1/1">
        <Layout.Item>
          <Checkbox
            disabled={disabled}
            id={name}
            name={name}
            checked={isChecked}
            onChange={() => handleCheckboxToggle(name, choices)}
          >
            {shouldUseGeneralLabel ? getAdditionalProductLabel(name) : relevantChoices?.[0]?.name}
          </Checkbox>
        </Layout.Item>

        {isTireProduct && <TireCount name={name} isChecked={isChecked} disabled={disabled} />}

        {choicesToRender && choicesToRender.length > 1 && (
          <Layout className="u-ml-small">
            {choicesToRender.map((choice) =>
              renderChoice(choice, name, isWarrantyExtensionAdditionalProduct(name) ? '1/3' : layoutFraction)
            )}
          </Layout>
        )}

        {isWarrantyExtensionAdditionalProduct(name) && (
          <WarrantyExtensionAdditionalProductDetails disabled={disabled} isChecked={isChecked} />
        )}
      </Layout.Item>
    );
  };

  return (
    <Layout>
      {nonTireAndNonFuelProducts.map(renderAdditionalProduct)}
      {tireProducts.map(renderAdditionalProduct)}

      <Layout.Item>
        {shouldFuelServiceBeVisible && (
          <FuelService
            isFormDisabled={disabled}
            selectedAdditionalProducts={selectedAdditionalProducts}
            setSelectedAllAdditionalProducts={setSelectedAllAdditionalProducts}
            managementFees={managementFees}
            fuelDepositAmount={fuelDepositAmount}
            resetFuelPrices={resetFuelPrices}
          />
        )}
      </Layout.Item>
    </Layout>
  );
};

export const PrintAndSendEmailButtons: FunctionComponent<Props> = ({
  t,
  setOpenModal,
  setInfoToRender,
  docPrintingList,
}) => {
  const handleConfirm = () => {
    const selectedDocuments = docPrintingList.filter((document) => document.checked);
    const docNames = selectedDocuments.map((document) => document.name);
    const docNamesString = docNames.join(' | ');

    Analytics.push(getPrintAndSendEmailButtonsEvent(docNamesString));
  };
  return (
    <ButtonContainer center>
      <Button
        type="button"
        onClick={() => {
          setOpenModal(true);
          setInfoToRender(InfoToRenderType.DOCUMENTS_PRINTING);
        }}
      >
        {t('step3:print:label')}
      </Button>
      <Button onClick={handleConfirm} type="button">
        {t('step3:sendEmail:label')}
      </Button>
    </ButtonContainer>
  );
};

interface SaveAndQuoteDocumentsButtonsProps {
  setOpenModal: SetState<boolean>;
  setOpenCopyQuoteModal: SetState<boolean>;
  setInfoToRender: SetState<any>;
  shouldCalculate?: boolean;
}

export const SaveAndQuoteDocumentsButtons: FunctionComponent<SaveAndQuoteDocumentsButtonsProps> = ({
  setOpenModal,
  setInfoToRender,
  setOpenCopyQuoteModal,
  shouldCalculate,
}) => {
  const { validateForm } = useFormikContext<QuotationFormData>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const quoteId = assertDefined(useSelector(quoteSelectors.selectQuoteId));
  const quoteData = assertDefined(useSelector(quoteSelectors.selectQuote));
  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);
  const isQuoteUnlockable = useSelector(quoteSelectors.selectIsQuoteUnlockable);
  const isQuoteSavedOrValidated = useSelector(quoteSelectors.selectIsQuoteSavedOrValidated);

  const isCopyBlocked = useMemo(() => {
    const statusId = quoteData.financialProduct?.offers?.at(0)?.statusId;
    return isQuoteEditable || preventCopyQuoteStatuses.includes(statusId!);
  }, [isQuoteEditable, quoteData]);

  const onSave = async () => {
    try {
      await validateForm();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Quotation page form validation failed: ', e);
      return;
    }

    try {
      const quoteFragment = await validateQuote(quoteId);
      dispatch(updateQuoteIdAndStatus(quoteFragment));
    } catch {
      navigate.error();
    }
  };

  const onModify = async () => {
    const quoteId = assertDefined(staticSelector(quoteSelectors.selectQuoteId), 'Get quote to modify.');
    try {
      await editQuote(quoteId);
    } catch {
      navigate.error();
    }
  };

  return (
    <>
      <Layout className="o-layout--inline-center">
        <Layout.Item className="o-layout--inline-center">
          <ButtonContainer className="o-layout--inline-center">
            <Button
              type="button"
              disabled={shouldCalculate || isQuoteUnEditable(quoteData)}
              iconReversed
              secondary
              icon={isQuoteSavedOrValidated ? 'lock-open' : 'lock'}
              onClick={async () => {
                if (isQuoteSavedOrValidated) {
                  await onModify();
                } else {
                  await onSave();
                }
              }}
            >
              {isQuoteUnlockable ? t('step3:saveTheQuotation:modify') : t('step3:saveTheQuotation:label')}
            </Button>
            <Button
              icon="documents"
              iconReversed
              full
              disabled={isQuoteEditable}
              onClick={async () => {
                setOpenModal(true);
                setInfoToRender(InfoToRenderType.DOCUMENTS_PRINTING);
              }}
              type="button"
              secondary
            >
              {t('step3:documents:label')}
            </Button>
            <Button
              type="button"
              disabled={isCopyBlocked}
              iconReversed
              icon="divide"
              onClick={() => {
                setOpenCopyQuoteModal(true);
              }}
              secondary
            >
              {t('step3:copyTheQuotation:label')}
            </Button>
          </ButtonContainer>
        </Layout.Item>
      </Layout>
    </>
  );
};

/**
 * Days of the month at which withdrawal/payment should occur.
 */
export const withdrawalDates = {
  '1': '01',
  '5': '05',
  '15': 15,
  '20': 20,
  '25': 25,
} as const;

export const WithdrawalDate: FunctionComponent<Props> = ({ t, isDisabled }) => {
  return (
    <FormField
      name="withdrawalDate"
      type="other"
      labelText={t('step3:withdrawalDate:label')}
      render={(fieldProps) => (
        <SelectBR disabled={isDisabled} {...fieldProps}>
          {Object.values(withdrawalDates).map((e) => (
            <SelectBR.Item key={`${e}`} value={`${e}`}>
              {`${e}`}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};
